import { SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { getSaleDepositBranchSummary } from "../../services";
import {
  BranchDaySummary,
  BranchDaySummaryTotals,
  SaleDepositBranchSummaryFilter,
  SaleDepositReviewStatusFilter,
} from "../../types/sale-deposit.type";
import { SaleCenterClosingStatusFilter } from "../../types/sale-center-closing.type";
import { formatNumber, numberWithCommas } from "../../utils";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ReportPagination from "../Report/ReportList/Pagination";
import BranchSummaryFilter from "./BranchSummaryFilter";
import BranchSummaryFooter from "./BranchSummaryFooter";
import ClosingStatusChip from "./ClosingStatusChip";
import DiffAmountText from "./DiffAmountText";
import NetOfNotesHint from "./NetOfNotesHint";
import TableSkeletonRows from "./TableSkeletonRows";
import TransferAmountText from "./TransferAmountText";
import { isReviewStatusFilter } from "./Filter";
import { isClosingStatusFilter } from "./saleCenterClosing.utils";
import {
  DEPOSIT_REPORT_PARAM,
  branchDayDetailUrl,
} from "./depositReportUrl";
import {
  DATE_COLUMN_WIDTH,
  STICKY_BODY_Z_INDEX,
  STICKY_HEADER_Z_INDEX,
  stickyColumnSx,
} from "./tableLayout";
import {
  CASH_IN_HAND_HINT,
  CASH_IN_HAND_LABEL,
  formatCashInHand,
  pendingSlipHint,
  physicalCash,
} from "./transfer.utils";

const COLUMN_COUNT = 16;
const LIMIT = 10;

const API_DATE_FORMAT = "YYYY-MM-DD";
const DISPLAY_DATE_FORMAT = "DD/MM/YYYY";

/** The value every filter uses for "ไม่กรอง" — dropped from the URL rather than written out. */
const UNFILTERED_OPTION_VALUE = "all";

/** A hand-edited or stale query param must not seed the picker with an invalid day. */
const parseDateParam = (value: string | null): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.startOf("day") : null;
};

const parsePageParam = (value: string | null): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

/**
 * รายงานการโอน as head office reads it: ONE row per สาขา per วัน, not one per สายส่ง.
 *
 * Twenty branches running three routes each produced sixty-odd rows of a report the supervisor
 * works through a branch at a time. Here they are twenty rows for a single day — and forty for
 * two, because ปิดศูนย์เซล is decided per branch-day and a row spanning both days could not say
 * whether the money is closed. Clicking a row opens that branch-day's routes.
 *
 * The filters live in the query string rather than in state, which is what makes the round trip
 * free: the drill-down carries the same query string, so ย้อนกลับ — and the browser's own back
 * button — return to the day and filters already chosen instead of a reset screen.
 */
export default function BranchDaySummaryTable() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [limit, setLimit] = useState<number>(LIMIT);
  const [rows, setRows] = useState<BranchDaySummary[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  // Server-computed over every matching branch-day, not the page — see BranchSummaryFooter.
  const [totals, setTotals] = useState<BranchDaySummaryTotals | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const dateFromParam = searchParams.get(DEPOSIT_REPORT_PARAM.dateFrom);
  const dateToParam = searchParams.get(DEPOSIT_REPORT_PARAM.dateTo);
  const branchParam = searchParams.get(DEPOSIT_REPORT_PARAM.branchCode);
  const statusParam = searchParams.get(DEPOSIT_REPORT_PARAM.status);
  const closingStatusParam = searchParams.get(
    DEPOSIT_REPORT_PARAM.closingStatus,
  );

  // Memoized on the raw params so the Dayjs identities stay stable between renders; without
  // that the filter below would be a new object every render and refetch in a loop.
  const dateFrom = useMemo(
    () => parseDateParam(dateFromParam) ?? dayjs().startOf("day"),
    [dateFromParam],
  );
  const dateTo = useMemo(
    () => parseDateParam(dateToParam) ?? dayjs().startOf("day"),
    [dateToParam],
  );
  const selectedBranch = branchParam || UNFILTERED_OPTION_VALUE;
  const status = isReviewStatusFilter(statusParam)
    ? statusParam
    : UNFILTERED_OPTION_VALUE;
  const closingStatus = isClosingStatusFilter(closingStatusParam)
    ? closingStatusParam
    : UNFILTERED_OPTION_VALUE;
  const page = parsePageParam(searchParams.get(DEPOSIT_REPORT_PARAM.page));

  const filter = useMemo<SaleDepositBranchSummaryFilter>(
    () => ({
      dateFrom: dateFrom.format(API_DATE_FORMAT),
      dateTo: dateTo.format(API_DATE_FORMAT),
      branchCode: selectedBranch,
      status,
      closingStatus,
    }),
    [dateFrom, dateTo, selectedBranch, status, closingStatus],
  );

  /**
   * Writes filter values into the query string. An unfiltered value is deleted rather than
   * written so the URL carried into a drill-down stays readable, and the page is dropped
   * because a changed filter changes the result set the old page number pointed into.
   *
   * `replace` throughout: the history entry the user wants back is the one they drilled in
   * from, not every intermediate state of the filter row.
   */
  const updateFilterParams = useCallback(
    (changes: Record<string, string>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          Object.entries(changes).forEach(([key, value]) => {
            if (!value || value === UNFILTERED_OPTION_VALUE) {
              next.delete(key);
              return;
            }
            next.set(key, value);
          });
          next.delete(DEPOSIT_REPORT_PARAM.page);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const changeDateRange = useCallback(
    (from: Dayjs, to: Dayjs) => {
      updateFilterParams({
        [DEPOSIT_REPORT_PARAM.dateFrom]: from.format(API_DATE_FORMAT),
        [DEPOSIT_REPORT_PARAM.dateTo]: to.format(API_DATE_FORMAT),
      });
    },
    [updateFilterParams],
  );

  const changeBranch = useCallback(
    (branchCode: string) => {
      updateFilterParams({ [DEPOSIT_REPORT_PARAM.branchCode]: branchCode });
    },
    [updateFilterParams],
  );

  const changeStatus = useCallback(
    (nextStatus: SaleDepositReviewStatusFilter) => {
      updateFilterParams({ [DEPOSIT_REPORT_PARAM.status]: nextStatus });
    },
    [updateFilterParams],
  );

  const changeClosingStatus = useCallback(
    (nextStatus: SaleCenterClosingStatusFilter) => {
      updateFilterParams({ [DEPOSIT_REPORT_PARAM.closingStatus]: nextStatus });
    },
    [updateFilterParams],
  );

  // ReportPagination owns a state setter's signature, so an updater has to be resolved
  // against the page currently in the URL before it can be written back to it.
  const changePage = useCallback(
    (value: SetStateAction<number>) => {
      const nextPage = typeof value === "function" ? value(page) : value;
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (nextPage <= 1) {
            next.delete(DEPOSIT_REPORT_PARAM.page);
          } else {
            next.set(DEPOSIT_REPORT_PARAM.page, String(nextPage));
          }
          return next;
        },
        { replace: true },
      );
    },
    [page, setSearchParams],
  );

  const fetchBranchDays = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getSaleDepositBranchSummary(page, limit, filter);
      setRows(data?.rows ?? []);
      setTotalCount(data?.count ?? 0);
      setTotals(data?.totals ?? null);
    } catch {
      // The interceptor has already reported the failure; empty out rather than leaving
      // figures from the previous filter on screen under the new one.
      setRows([]);
      setTotalCount(0);
      setTotals(null);
    } finally {
      setLoading(false);
    }
  }, [page, limit, filter]);

  useEffect(() => {
    fetchBranchDays();
  }, [fetchBranchDays]);

  // Curried so each row binds its own branch-day without an inline arrow in JSX. The current
  // query string travels with it as the return ticket ย้อนกลับ reads back.
  const openBranchDay = (row: BranchDaySummary) => () => {
    navigate(
      branchDayDetailUrl(
        row.branch_code,
        row.business_date,
        searchParams.toString(),
      ),
    );
  };

  return (
    <Paper>
      <BranchSummaryFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onChangeDateRange={changeDateRange}
        selectedBranch={selectedBranch}
        onChangeBranch={changeBranch}
        status={status}
        onChangeStatus={changeStatus}
        closingStatus={closingStatus}
        onChangeClosingStatus={changeClosingStatus}
      />
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table stickyHeader>
          <TableHead
            sx={{
              "& th": {
                bgcolor: (theme) => theme.palette.background.default,
                color: (theme) => theme.palette.gray.main,
                fontWeight: 600,
              },
            }}
          >
            <TableRow sx={{ whiteSpace: "nowrap" }}>
              <TableCell
                align="center"
                sx={{
                  ...stickyColumnSx(0, STICKY_HEADER_Z_INDEX),
                  minWidth: DATE_COLUMN_WIDTH,
                }}
              >
                วันที่
              </TableCell>
              <TableCell
                align="center"
                sx={stickyColumnSx(DATE_COLUMN_WIDTH, STICKY_HEADER_Z_INDEX)}
              >
                สาขา
              </TableCell>
              <TableCell align="center">จำนวนสาย</TableCell>
              <TableCell align="center">จำนวนร้านค้า</TableCell>
              <TableCell align="right">ยอดรวม</TableCell>
              <TableCell align="right">ยอดเงินเชื่อ</TableCell>
              {/* Same column, same hint as the route-level table: cash in hand is whole
                  baht, and รวมเงินสด below is this plus เงินโอน. */}
              <TableCell align="right">
                <Tooltip title={CASH_IN_HAND_HINT}>
                  <Box
                    component="span"
                    sx={{ borderBottom: "1px dotted", cursor: "help" }}
                  >
                    {CASH_IN_HAND_LABEL}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell align="right">เงินโอน</TableCell>
              <TableCell align="right">รวมเงินสด</TableCell>
              <TableCell align="right">ยอดฝากเก็บ</TableCell>
              <TableCell align="right">ค่าใช้จ่าย</TableCell>
              <TableCell align="right">ที่ต้องโอน/ยอดโอน</TableCell>
              <TableCell align="right">ผลต่าง</TableCell>
              <TableCell align="center">ตรวจแล้ว (BO)</TableCell>
              <TableCell align="center">ตรวจแล้ว (HO)</TableCell>
              <TableCell align="center">สถานะการปิดยอด</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableSkeletonRows rowCount={limit} columnCount={COLUMN_COUNT} />
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูล
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((row, index) => (
                <TableRow
                  key={`${row.branch_code}-${row.business_date}`}
                  onClick={openBranchDay(row)}
                  sx={{
                    cursor: "pointer",
                    bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                    "& td, & th": { padding: "8px" },
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <TableCell
                    align="center"
                    sx={stickyColumnSx(0, STICKY_BODY_Z_INDEX)}
                  >
                    {dayjs(row.business_date).format(DISPLAY_DATE_FORMAT)}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={stickyColumnSx(
                      DATE_COLUMN_WIDTH,
                      STICKY_BODY_Z_INDEX,
                    )}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {row.branch_code}
                    </Typography>
                    {row.branch_name && (
                      <Typography variant="caption" color="text.secondary">
                        {row.branch_name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {numberWithCommas(row.route_count)}
                  </TableCell>
                  <TableCell align="center">
                    {numberWithCommas(row.customer_count)}
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(row.total_amount)}
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(row.credit_total_value)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCashInHand(physicalCash(row))}
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(row.transfer_total_value)}
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(row.cash_total_value)}
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(row.deposit_collection_amount)}
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(row.total_expense_amount)}
                  </TableCell>
                  {/* A branch-day sums an unsent slip in as 0, so the routes still to remit
                      are named here rather than left to read as a shortfall. */}
                  <TableCell align="right">
                    <Tooltip title={pendingSlipHint(row.pending_count)}>
                      <span>
                        <TransferAmountText
                          required={row.required_transfer_amount}
                          transfer={row.transfer_amount}
                        />
                      </span>
                    </Tooltip>
                    <NetOfNotesHint totalAmount={row.credit_debit_note_amount} />
                  </TableCell>
                  <TableCell align="right">
                    <DiffAmountText diff={row.diff_amount} />
                  </TableCell>
                  {/* How far each sign-off has got through the branch-day's routes — the
                      aggregate of the two ตรวจแล้ว columns one level down. */}
                  <TableCell align="center">
                    {numberWithCommas(row.bo_reviewed_count)}/
                    {numberWithCommas(row.route_count)}
                  </TableCell>
                  <TableCell align="center">
                    {numberWithCommas(row.ho_reviewed_count)}/
                    {numberWithCommas(row.route_count)}
                  </TableCell>
                  <TableCell align="center">
                    <ClosingStatusChip status={row.closing_status} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
          <BranchSummaryFooter
            totals={totals}
            loading={loading}
            columnCount={COLUMN_COUNT}
          />
        </Table>
      </TableContainer>
      <ReportPagination
        totalCount={totalCount}
        page={page}
        setPage={changePage}
        limit={limit}
        setLimit={setLimit}
      />
    </Paper>
  );
}
